import { getAcidFromCookie, resolveValidAcid } from "../hooks/useAcid";

const PAGE_NAME_MAPPING = {
  "/campaign": "Experience Page",
  "/multiCampaign": "Multi-Experience Page",
  "/share/explore": "Explore Page (Bubbles Experience)",
  "/follow/explore": "Explore Page (Follow Experience)",
  "/travel/explore": "Explore Page (Visual Experience)",
  "/derived-visual/explore": "Explore Page (Derived Visual Experience)",
  "/video/explore": "Explore Page (Video Experience)",
  "/offers": "Offers Landing Page",
  "/consent/thank-you": "Consent Thank You Page",
  "/beverage": "Beverage Landing Page",
  "/beverage/preferences": "Beverage Preferences Page",
  "/beverage/thank-you": "Thank You Page (Beverage)",
  "/beverage/explore": "Explore Page (Beverage Experience)",
  "/beverage2": "Beverage V2 AI Input Page",
  "/beverage2/confirm": "Beverage V2 Confirm Page",
  "/beverage2/opt-confirm": "Passions Opt-In Confirm Page (Beverage V2)",
  "/beverage2/save": "Beverage V2 Save Page",
  "/beverage2/thank-you": "Thank You Page (Beverage V2)",
  "/": "Root (Not Found) Page",
};

let cachedUser = {
  alt_customer_id: "",
  authentication_status: "",
  passions: null,
  bonvoy_level: null,
};

let flushPendingPageView = null;
let isFlushingPendingPageView = false;

export const getAdobeDataLayer = () => {
  window.adobeDataLayer = window.adobeDataLayer || [];
  return window.adobeDataLayer;
};

/**
 * Sets the cached user data for analytics events.
 * @param {object} userData - User data object with alt_customer_id, authentication_status, passions, bonvoy_level
 */
export const setAnalyticsUser = (userData) => {
  if (userData.alt_customer_id !== undefined) {
    cachedUser.alt_customer_id = userData.alt_customer_id;
  }
  if (userData.authentication_status !== undefined) {
    cachedUser.authentication_status = userData.authentication_status;
  }
  if (userData.passions !== undefined) {
    cachedUser.passions = userData.passions;
  }
  if (userData.bonvoy_level !== undefined) {
    cachedUser.bonvoy_level = userData.bonvoy_level;
  }
};

/**
 * Resolves the ACID for analytics using the same order as useAcid()
 * (URL, dtm_user_id cookie, then sessionStorage), ensuring the first
 * page view reports the correct alt_customer_id even before useAcid()
 * has populated sessionStorage.
 */
const getAcidFromContext = () => {
  try {
    const hashSearch = window.location.hash.split("?")[1];
    const acidFromLocation = hashSearch
      ? new URLSearchParams(hashSearch).get("acid")
      : null;

    const { acid } = resolveValidAcid([
      { value: acidFromLocation, source: "URL param", stopOnInvalid: true },
      {
        value: getAcidFromCookie("dtm_user_id"),
        source: "dtm_user_id cookie",
      },
      { value: sessionStorage.getItem("acid"), source: "sessionStorage" },
    ]);

    return acid || "";
  } catch (e) {
    return "";
  }
};

const getPageSnapshot = () => {
  try {
    const currentLocation = window.location;
    const pathname = currentLocation.pathname;

    return {
      url:
        currentLocation.host +
        currentLocation.pathname +
        "#" +
        currentLocation.hash.slice(1).split("?")[0],
      name: PAGE_NAME_MAPPING[pathname] || pathname,
    };
  } catch (e) {
    return {
      url: "",
      name: "Marriott Bonvoy Passions",
    };
  }
};

const getCommonAnalyticsData = (
  triggerName,
  customSiteId,
  pageSnapshot = getPageSnapshot(),
) => {
  let pageName = "Marriott Bonvoy Passions";
  if (pageSnapshot?.name) {
    pageName = pageSnapshot.name;
  }

  // Use cached value if available, otherwise try to get from URL/sessionStorage
  const altCustomerId = cachedUser.alt_customer_id || getAcidFromContext();

  const userData = {
    alt_customer_id: altCustomerId,
    authentication_status: cachedUser.authentication_status,
  };

  if (cachedUser.passions) {
    userData.passions = cachedUser.passions;
  }

  if (cachedUser.bonvoy_level) {
    userData.bonvoy_level = cachedUser.bonvoy_level;
  }

  return {
    metadata: {
      site_id: customSiteId ?? "Passions Project",
      allow_marketing: false,
      language_domain: "US",
      platform: "App Builder",
    },
    page: {
      url: pageSnapshot?.url || "",
      name: pageName,
    },
    user: userData,
    trigger: {
      name: triggerName,
    },
  };
};

/**
 *
 * @param {string} componentName - The name of the component (e.g., "Bubbles")
 * @param {string} interactionType - The type of interaction (e.g., "internal", "external")
 * @param {string} details - Specific details (e.g., label/text)
 * @returns {object} Props object to spread onto the element
 */
export const getAnalyticsClickTrackingAttributes = (
  componentName,
  interactionType,
  details,
) => {
  const value = `${componentName}|${details}|${interactionType}`;
  return {
    "data-custom_click_track_value": value,
  };
};

/**
 * Pushes the PAGE_VIEW event to Adobe Data Layer.
 *
 * @param {Array} pageName - Сustom pagename. Property is optional.
 */
export const sendAnalyticsPageViewEvent = (
  pageName,
  customSiteId,
  template,
  pageSnapshot,
) => {
  if (flushPendingPageView && !isFlushingPendingPageView) {
    flushPendingPageView();
  }

  const adobeDataLayer = getAdobeDataLayer();

  // Use existing common data logic but adapt it to the specific PAGEVIEW schema
  const commonData = getCommonAnalyticsData(
    "PAGEVIEW",
    customSiteId,
    pageSnapshot,
  );

  // Construct the compliant payload
  const payload = {
    event: "PAGEVIEW",
    metadata: commonData.metadata,
    page: {
      name: commonData.page.url, // In reference, name is the URL/hostname path
      section: pageName || commonData.page.name, // In reference, section is the friendly name
      template,
    },
    user: {
      alt_customer_id: commonData.user.alt_customer_id,
      // authentication_status: commonData.user.authentication_status,
      authentication_status: "Unauthenticated",
      // Retain extra user data if present, though strictly reference suggests simpler specific keys
      passions: commonData.user.passions,
      bonvoy_level: commonData.user.bonvoy_level,
    },
    // Reference includes products array
    products: [],
  };

  adobeDataLayer.push(payload);
};

export const sendPageViewWhenConsentReady = (
  pageName,
  customSiteId,
  template,
  delayMs = 500,
) => {
  let hasSentPageView = false;
  let pageViewTimer;
  const pageSnapshot = getPageSnapshot();

  const sendOnce = () => {
    if (hasSentPageView) {
      return;
    }

    hasSentPageView = true;
    clearTimeout(pageViewTimer);
    window.removeEventListener("OnConsentChanged", sendOnce);
    if (flushPendingPageView === sendOnce) {
      flushPendingPageView = null;
    }

    isFlushingPendingPageView = true;
    try {
      sendAnalyticsPageViewEvent(
        pageName,
        customSiteId,
        template,
        pageSnapshot,
      );
    } finally {
      isFlushingPendingPageView = false;
    }
  };

  if (flushPendingPageView) {
    flushPendingPageView();
  }
  flushPendingPageView = sendOnce;
  window.addEventListener("OnConsentChanged", sendOnce);
  pageViewTimer = setTimeout(sendOnce, delayMs);

  return () => {
    sendOnce();
  };
};
